<script setup lang="ts">
/**
 * ImpactSection —— `.scu-widget` 壳 + DataCard 数字面板（2026-06-25 R1/R6）。
 *
 * 对齐模板「应用效果」= 1 个 `.scu-widget` + `.scu-grid` 内 3 个
 * `.scu-grid-item`：SectionCard 提供 widget 壳，每张 DataCard 渲染为
 * 一个 GridItem（h4 = name，内含 value/unit 数字 + description）。
 *
 * DataCard 是 aggregate 顶层数字区，不属于单个 Section；多个 impact
 * Section 时 HomePage 只把 dataCards 传给首个 impact Section，避免
 * 同一组全局数字重复显示（F05 acceptance）。
 */
import { computed } from 'vue';

import SectionCard from './SectionCard.vue';
import GridItem from './GridItem.vue';
import DetailMoreLink from '@/components/DetailMoreLink.vue';
import type { SectionComponentProps } from './section-props';
import { copy } from '@/i18n/zh-CN';
import { hasText } from './section-presenters';

const props = defineProps<SectionComponentProps>();
const cards = computed(() => props.dataCards ?? []);

function hasMetric(card: { readonly value: string; readonly unit: string }): boolean {
  return hasText(card.value) || hasText(card.unit);
}
</script>

<template>
  <SectionCard
    :id="props.id"
    type="impact"
    :title="props.section.title"
    :summary="props.section.summary"
    :show-draft-badge="props.showDraftBadge"
  >
    <template #more>
      <DetailMoreLink :section="props.section" :preview-token="props.previewToken" />
    </template>
    <div v-if="cards.length > 0" :class="$style.grid" data-shell="scu-grid">
      <GridItem
        v-for="card in cards"
        :key="card.id"
        :title="card.name"
      >
        <p v-if="hasMetric(card)" :class="$style.value">
          <span>{{ card.value }}</span>
          <small v-if="hasText(card.unit)">{{ card.unit }}</small>
        </p>
        <p v-if="hasText(card.description)" :class="$style.description">
          {{ card.description }}
        </p>
      </GridItem>
    </div>
    <p v-else :class="$style.empty">{{ copy.sections.empty }}</p>
  </SectionCard>
</template>

<style module>
.grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--template-grid-gap);
}

.value {
  margin: 0 0 var(--space-1);
  color: var(--color-brand-primary);
  font-size: var(--font-size-xl);
  font-weight: 700;
}

.value small {
  margin-left: var(--space-1);
  color: var(--color-brand-accent);
  font-size: var(--font-size-md);
}

.description {
  margin: 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
}

.empty {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-sm);
}

</style>
